import { components, type GroupBase, type MenuListProps } from 'react-select';

import classNames from 'classnames';

import { isNotDefined } from '@enterprise/core';
import { useScrollToEdges } from '@enterprise/hooks';

import { useVirtual } from '#select/hooks/virtual.hook';

/**
 * A custom MenuList component for react-select that implements virtualized rendering
 * for improved performance with large option lists.
 *
 * This component uses a custom `useVirtual` hook to only render visible options,
 * reducing DOM node count and improving scroll performance.
 *
 * @template Option - The type of the option objects.
 * @template IsMulti - Boolean indicating if multi-select is enabled.
 * @template Group - The type of the group objects.
 *
 * @param props - The props for the MenuList, including children, className, innerRef, and others from react-select.
 * @returns A virtualized MenuList component for use within react-select.
 */
function VirtualMenuList<Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  children,
  className,
  innerRef: _,
  ...props
}: MenuListProps<Option, IsMulti, Group>) {
  const { options, selectProps } = props;
  const { flattenedChildren, items, measureRef, parentRef, totalSize } = useVirtual({
    children,
    options,
  });

  function adaptEventHandler<TEvent extends Event>(handler?: (event: TEvent) => void): (() => void) | undefined {
    if (isNotDefined(handler)) {
      return undefined;
    }

    return () => {
      handler(undefined as unknown as TEvent);
    };
  }

  useScrollToEdges(parentRef, {
    onScrollToBottom: adaptEventHandler(selectProps.onMenuScrollToBottom),
    onScrollToTop: adaptEventHandler(selectProps.onMenuScrollToTop),
  });

  return (
    <components.MenuList
      className={classNames(className, 'hsp-select-virtual-menu-list')}
      innerRef={parentRef}
      {...props}>
      <div
        className="hsp-select-virtual-menu-list-wrapper"
        style={{ height: totalSize }}>
        <div
          className="hsp-select-virtual-option-wrapper"
          style={{ transform: `translateY(${items[0]?.start ?? 0}px)` }}>
          {items.map((virtualRow) => {
            return (
              <div
                data-index={virtualRow.index}
                key={virtualRow.index}
                ref={measureRef}>
                {flattenedChildren[virtualRow.index]}
              </div>
            );
          })}
        </div>
      </div>
    </components.MenuList>
  );
}

export default VirtualMenuList;
